import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutGrid, Users, Target, Network, Shield, LogOut, Sparkles, Rss, UsersRound, Globe2, CalendarDays } from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth, useLang } from "@/context/AppContext";
import { Logo } from "@/components/Logo";
import { LangSwitch } from "@/components/LangSwitch";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const AIConsent = () => {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [ask, setAsk] = useState(false);
  const save = async (consent) => {
    try {
      await api.post("/ai/consent", { consent });
      setUser({ ...user, ai_consent: consent });
      toast.success(t(consent ? "ai_on" : "ai_off"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-2">
      <Sparkles className={`h-4 w-4 ${user.ai_consent ? "text-emerald-400" : "text-slate-500"}`} />
      <span className="hidden font-mono text-[11px] uppercase tracking-widest text-slate-400 sm:inline">{t("ai_consent")}</span>
      <Switch data-testid="ai-consent-toggle" checked={!!user.ai_consent} onCheckedChange={(v) => (v ? setAsk(true) : save(false))} />
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent className="border-white/10 bg-[#111827]" data-testid="ai-consent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("ai_warn_title")}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">{t("ai_warn_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ai-consent-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="ai-consent-accept" onClick={() => save(true)}>{t("ai_accept")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default function AppShell() {
  const { t } = useLang();
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  useEffect(() => { refresh(); }, [refresh]);
  const links = [["/app", "app_dashboard", LayoutGrid, true], ["/app/feed", "app_feed", Rss], ["/app/communities", "app_communities", UsersRound], ["/app/globe", "app_globe", Globe2], ["/app/agenda", "app_agenda", CalendarDays], ["/app/principals", "app_principals", Users], ["/app/intents", "app_intents", Target], ["/app/connections", "app_connections", Network]];
  if (user.role === "admin") links.push(["/app/admin", "app_admin", Shield]);

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <header className="sticky top-0 z-30 glass border-b border-white/5">
        <div className="flex items-center justify-between px-4 py-3 sm:px-6">
          <NavLink to="/" data-testid="app-home-link"><Logo /></NavLink>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <AIConsent />
            <LangSwitch />
            <span data-testid="user-role-badge" className={`rounded-full border px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-widest ${user.role === "admin" ? "border-yellow-400/40 text-yellow-300" : "border-cyan-400/40 text-cyan-300"}`}>{user.role}</span>
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="text-slate-400 transition-colors hover:text-white" title={t("logout")}>
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <div className="flex">
        <aside className="sticky top-[57px] hidden h-[calc(100vh-57px)] w-56 shrink-0 border-r border-white/5 p-4 md:block">
          <nav className="space-y-1">
            {links.map(([to, k, Icon, end]) => (
              <NavLink key={to} to={to} end={end} data-testid={`sidebar-${k}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-200 ${isActive ? "bg-emerald-400/10 text-emerald-300" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
                <Icon className="h-4 w-4" strokeWidth={1.5} /> {t(k)}
              </NavLink>
            ))}
          </nav>
          <p className="absolute bottom-6 left-4 right-4 font-mono text-[10px] uppercase leading-relaxed tracking-widest text-yellow-300/70">{t("rule")}</p>
        </aside>
        <nav className="fixed inset-x-0 bottom-0 z-30 flex justify-around border-t border-white/5 glass py-2 md:hidden">
          {links.map(([to, k, Icon, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`mobile-nav-${k}`} className={({ isActive }) => `p-2 ${isActive ? "text-emerald-300" : "text-slate-400"}`}><Icon className="h-5 w-5" /></NavLink>
          ))}
        </nav>
        <main className="min-w-0 flex-1 px-4 py-8 pb-24 sm:px-8 lg:px-12"><Outlet /></main>
      </div>
    </div>
  );
}
